const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3333/api/v1";

export function apiAssetUrl(assetId: string): string {
  return `${API_URL}/assets/${encodeURIComponent(assetId)}/content`;
}

export class ApiError extends Error {
  code: string;
  status: number;
  correlationId?: string;
  details: unknown[];

  constructor(status: number, payload: any) {
    super(payload?.error?.message ?? "Não foi possível concluir a operação.");
    this.name = "ApiError";
    this.status = status;
    this.code = payload?.error?.code ?? "UNKNOWN_ERROR";
    this.correlationId = payload?.error?.correlationId;
    this.details = payload?.error?.details ?? [];
  }
}

function readCookie(name: string): string | undefined {
  const prefix = `${encodeURIComponent(name)}=`;
  return document.cookie
    .split("; ")
    .find((part) => part.startsWith(prefix))
    ?.slice(prefix.length);
}

type ApiInit = Omit<RequestInit, "body"> & { body?: unknown; agentTurnId?: string };

function prepareRequest(init: ApiInit): { headers: Headers; body: BodyInit | null | undefined } {
  const headers = new Headers(init.headers);
  const method = (init.method ?? "GET").toUpperCase();
  let body = init.body;
  if (body && !(body instanceof FormData) && typeof body !== "string") {
    headers.set("content-type", "application/json");
    body = JSON.stringify(body);
  }
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrfToken = readCookie("calcplan_csrf");
    if (csrfToken) headers.set("x-csrf-token", decodeURIComponent(csrfToken));
  }
  if (init.agentTurnId) headers.set("x-agent-turn-id", init.agentTurnId);
  return { headers, body: body as BodyInit | null | undefined };
}

export async function api<T>(
  path: string,
  init: ApiInit = {},
): Promise<T> {
  const { headers, body } = prepareRequest(init);
  const { agentTurnId: _agentTurnId, ...requestInit } = init;

  const response = await fetch(`${API_URL}${path}`, {
    ...requestInit,
    body: body as BodyInit | null | undefined,
    headers,
    credentials: "include",
  });
  if (response.status === 204) return undefined as T;
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(response.status, payload);
  return payload as T;
}

/** Reads the neutral SSE stream used by one step of the agent loop. */
export async function apiEventStream<T>(
  path: string,
  init: ApiInit,
  onEvent: (event: T) => void,
): Promise<void> {
  const prepared = prepareRequest(init);
  prepared.headers.set("accept", "text/event-stream");
  const { agentTurnId: _agentTurnId, ...requestInit } = init;
  const response = await fetch(`${API_URL}${path}`, {
    ...requestInit,
    body: prepared.body,
    headers: prepared.headers,
    credentials: "include",
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new ApiError(response.status, payload);
  }
  if (!response.body) throw new Error("O navegador não recebeu o fluxo da resposta.");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const frames = buffer.split(/\r?\n\r?\n/);
    buffer = frames.pop() ?? "";
    for (const frame of frames) {
      const data = frame
        .split(/\r?\n/)
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trimStart())
        .join("\n");
      if (!data) continue;
      onEvent(JSON.parse(data) as T);
    }
    if (done) break;
  }
}
